import { useState, useRef, useCallback } from 'react';
import { TextInput } from '@c2fo/liquidity';
import { UploadIcon } from '@c2fo/liquidity/icons';
import { FieldHelpTooltip } from '../FieldHelpTooltip';
import { validateFilePath, suggestFilePath } from '../../api/Credentials/validation';
import type { HelpSeverity } from '../../api/Credentials/types';

interface CredentialFileFieldProps {
  label: string;
  pathValue: string;
  onPathChange: (path: string) => void;
  onFileContent: (content: string, filename: string) => void;
  helpText: string;
  helpSeverity?: HelpSeverity;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

export const CredentialFileField: React.FC<CredentialFileFieldProps> = ({
  label,
  pathValue,
  onPathChange,
  onFileContent,
  helpText,
  helpSeverity = 'warning',
  required = false,
  placeholder = 'e.g., my-entity/filename',
  disabled = false,
  error: externalError,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFilename, setUploadedFilename] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pathError = validateFilePath(pathValue, label, required);
  const displayError = externalError || pathError?.message;

  const handleFileSelect = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const content = reader.result as string;
        onFileContent(content, file.name);
        setUploadedFilename(file.name);
        onPathChange(suggestFilePath(file.name));
      };

      reader.readAsText(file);
    },
    [onFileContent, onPathChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <FieldHelpTooltip helpText={helpText} severity={helpSeverity} />
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          isDragging ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-gray-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={disabled ? undefined : handleBrowseClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            handleBrowseClick();
          }
        }}
      >
        <input ref={fileInputRef} type="file" onChange={handleFileInputChange} className="hidden" disabled={disabled} />
        <UploadIcon className="h-6 w-6 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-600">
          {uploadedFilename ? (
            <>
              <span className="font-medium text-green-600">Uploaded:</span> {uploadedFilename}
            </>
          ) : (
            <>
              <span className="font-medium">Drop file here</span> or{' '}
              <span className="text-primary-600 underline">browse</span>
            </>
          )}
        </p>
      </div>

      <div>
        <span className="block text-xs font-medium text-gray-600 mb-1">File Path (where the file will be stored)</span>
        <TextInput
          value={pathValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPathChange(e.target.value)}
          placeholder={placeholder}
          className="w-full font-mono text-sm"
          size="sm"
          disabled={disabled}
        />
        {displayError && <p className="mt-1 text-xs text-red-600">{displayError}</p>}
        {!displayError && pathValue && pathValue.startsWith('/') && (
          <p className="mt-1 text-xs text-amber-600">
            ⚠️ Path must be relative. Remove the leading slash and add a namespace prefix.
          </p>
        )}
      </div>
    </div>
  );
};
